import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  Home,
  Users,
  ChevronRight,
  DollarSign,
  Calendar,
  UserX,
  Smile,
  Heart,
  Monitor,
  BarChart,
  Shield,
  MessageCircle,
} from 'lucide-react';

const SidebarContainer = styled.aside.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  width: ${({ isOpen }) => (isOpen ? '240px' : '60px')};
  max-width: ${({ isOpen }) => (isOpen ? '240px' : '60px')};
  min-width: ${({ isOpen }) => (isOpen ? '240px' : '60px')};
  background-color: ${({ theme }) => theme.colors.surface};
  border-right: 1px solid ${({ theme }) => theme.colors.border.light};
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 90;
  padding-top: 60px; // Header height
  overflow: hidden;
  box-sizing: border-box;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 240px;
    transform: ${({ isOpen }) =>
      isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 95;
    box-shadow: ${({ isOpen, theme }) => (isOpen ? theme.shadows.xl : 'none')};
  }
`;

const Navigation = styled.nav`
  padding: ${({ theme }) => theme.spacing.sm} 0;
  width: 100%;
  overflow: hidden;
`;

const NavSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const NavItem = styled.button.withConfig({
  shouldForwardProp: (prop) =>
    !['isActive', 'isOpen', 'disabled'].includes(prop),
})<{ isActive?: boolean; isOpen: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ isOpen, theme }) => (isOpen ? theme.spacing.md : '0')};
  width: 100%;
  padding: ${({ isOpen, theme }) =>
    isOpen
      ? `${theme.spacing.md} ${theme.spacing.lg}`
      : `${theme.spacing.md} ${theme.spacing.sm}`};
  color: ${({ isActive, disabled, theme }) =>
    disabled
      ? theme.colors.text.muted
      : isActive
      ? theme.colors.primary
      : theme.colors.text.secondary};
  background-color: ${({ isActive, disabled, theme }) =>
    disabled
      ? 'transparent'
      : isActive
      ? `${theme.colors.primary}10`
      : 'transparent'};
  border-left: 3px solid
    ${({ isActive, disabled, theme }) =>
      disabled
        ? 'transparent'
        : isActive
        ? theme.colors.primary
        : 'transparent'};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ isActive, theme }) =>
    isActive
      ? theme.typography.fontWeight.medium
      : theme.typography.fontWeight.normal};
  text-align: left;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  &:hover {
    ${({ disabled, theme }) =>
      !disabled &&
      `
      background-color: ${theme.colors.surfaceLight};
      color: ${theme.colors.text.primary};
    `}
  }

  .nav-text {
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
    width: ${({ isOpen }) => (isOpen ? 'auto' : '0')};
    overflow: hidden;
    transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1),
      width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    white-space: nowrap;
  }

  .nav-icon {
    min-width: 20px;
    width: 20px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    /* Indicador de submenu quando colapsado */
    &[data-has-submenu='true']::after {
      content: '▸';
      position: absolute;
      bottom: -2px;
      right: -4px;
      font-size: 8px;
      color: ${({ theme }) => theme.colors.primary};
      opacity: ${({ isOpen }) => (isOpen ? 0 : 1)};
      transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      line-height: 1;
    }
  }

  .expand-icon {
    margin-left: auto;
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
      opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1),
      width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    width: ${({ isOpen }) => (isOpen ? '12px' : '0')};
    overflow: hidden;
  }

  .expand-icon.expanded {
    transform: rotate(90deg);
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  top: 65px;
  right: 8px;
  width: 20px;
  height: 20px;
  background-color: ${({ theme }) => theme.colors.surfaceLight};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 101;
  box-shadow: ${({ theme }) => theme.shadows.sm};

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
    border-color: ${({ theme }) => theme.colors.primary};
    transform: scale(1.05);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const SubMenuContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isOpen', 'isExpanded'].includes(prop),
})<{ isOpen: boolean; isExpanded: boolean }>`
  max-height: ${({ isExpanded }) => (isExpanded ? '400px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1),
    margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  background-color: ${({ theme }) => theme.colors.surface};
  margin-left: ${({ isOpen }) => (isOpen ? '20px' : '0')};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  margin-top: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
`;

const CollapsedSubmenu = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isVisible'].includes(prop),
})<{ isVisible: boolean }>`
  position: fixed;
  left: 65px;
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  z-index: 1001;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
  transform: ${({ isVisible }) =>
    isVisible ? 'translateX(0)' : 'translateX(-10px)'};
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 240px;
  max-width: 300px;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  pointer-events: ${({ isVisible }) => (isVisible ? 'auto' : 'none')};

  /* Seta indicativa */
  &::before {
    content: '';
    position: absolute;
    left: -8px;
    top: 20px;
    width: 0;
    height: 0;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-right: 8px solid ${({ theme }) => theme.colors.surface};
  }

  &::after {
    content: '';
    position: absolute;
    left: -9px;
    top: 20px;
    width: 0;
    height: 0;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-right: 8px solid ${({ theme }) => theme.colors.border.light};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const SubmenuHeader = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const SubmenuItem = styled.button.withConfig({
  shouldForwardProp: (prop) => !['isActive', 'disabled'].includes(prop),
})<{ isActive?: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  color: ${({ isActive, disabled, theme }) =>
    disabled
      ? theme.colors.text.muted
      : isActive
      ? theme.colors.primary
      : theme.colors.text.primary};
  background-color: ${({ isActive, disabled, theme }) =>
    disabled
      ? 'transparent'
      : isActive
      ? `${theme.colors.primary}10`
      : 'transparent'};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ isActive, theme }) =>
    isActive
      ? theme.typography.fontWeight.medium
      : theme.typography.fontWeight.normal};
  text-align: left;
  border: none;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: 3px solid transparent;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  &:hover {
    ${({ disabled, theme }) =>
      !disabled &&
      `
      background-color: ${theme.colors.primary}10;
      color: ${theme.colors.primary};
      border-left-color: ${theme.colors.primary};
    `}
  }

  ${({ isActive, disabled, theme }) =>
    isActive &&
    !disabled &&
    `
    border-left-color: ${theme.colors.primary};
  `}
`;

const SubMenuItemButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['isActive', 'disabled'].includes(prop),
})<{ isActive?: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  color: ${({ isActive, disabled, theme }) =>
    disabled
      ? theme.colors.text.muted
      : isActive
      ? theme.colors.primary
      : theme.colors.text.primary};
  background-color: ${({ isActive, disabled, theme }) =>
    disabled
      ? 'transparent'
      : isActive
      ? `${theme.colors.primary}10`
      : 'transparent'};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ isActive, theme }) =>
    isActive
      ? theme.typography.fontWeight.medium
      : theme.typography.fontWeight.normal};
  text-align: left;
  transition: ${({ theme }) => theme.transitions.fast};
  border-left: 3px solid
    ${({ isActive, disabled, theme }) =>
      disabled
        ? 'transparent'
        : isActive
        ? theme.colors.primary
        : 'transparent'};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  &:hover {
    ${({ disabled, theme }) =>
      !disabled &&
      `
      background-color: ${theme.colors.primary}05;
      color: ${theme.colors.text.primary};
    `}
  }
`;

interface SubMenuItem {
  id: string;
  label: string;
  disabled?: boolean;
}

interface NavItemData {
  id: string;
  label: string;
  icon: React.ReactNode;
  isActive?: boolean;
  disabled?: boolean;
  subItems?: SubMenuItem[];
}

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  activeItem?: string;
  onItemClick?: (itemId: string) => void;
}

// Hook para detectar se é mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  return isMobile;
};

const navigationItems: NavItemData[] = [
  {
    id: 'dashboard',
    label: 'Início',
    icon: <Home size={20} />,
    // Página existe
  },
  {
    id: 'people',
    label: 'Pessoas',
    icon: <Users size={20} />,
    subItems: [
      { id: 'people-alerts', label: 'Alertas', disabled: true },
      { id: 'personal-data', label: 'Dados cadastrais', disabled: true },
      { id: 'home', label: 'Indicadores de perfil da população' }, // Página existe
      {
        id: 'leadership-indicators',
        label: 'Indicadores de perfil das lideranças',
        disabled: true,
      },
    ],
  },
  {
    id: 'performance',
    label: 'Desempenho',
    icon: <BarChart size={20} />,
    disabled: true,
    subItems: [],
  },
  {
    id: 'costs',
    label: 'Custos',
    icon: <DollarSign size={20} />,
    disabled: true,
    subItems: [
      { id: 'general-costs', label: 'Custos gerais', disabled: true },
      { id: 'absenteeism-cost', label: 'Custo absenteísmo', disabled: true },
      { id: 'turnover-cost', label: 'Custo turnover', disabled: true },
    ],
  },
  {
    id: 'absenteeism-menu',
    label: 'Absenteísmo',
    icon: <Calendar size={20} />,
    subItems: [
      { id: 'absenteeism-alerts', label: 'Alertas', disabled: true },
      { id: 'absenteeism-data', label: 'Absenteísmo', disabled: true },
      { id: 'medical-certificates', label: 'Atestados' }, // Página existe
    ],
  },
  {
    id: 'turnover',
    label: 'Turnover',
    icon: <UserX size={20} />,
    disabled: true,
    subItems: [
      { id: 'turnover-general-data', label: 'Dados gerais', disabled: true },
      {
        id: 'turnover-exit-interview',
        label: 'Turnover x entrevista de desligamento',
        disabled: true,
      },
    ],
  },
  {
    id: 'desligamentos-menu',
    label: 'Desligamentos',
    icon: <UserX size={20} />,
    subItems: [
      { id: 'desligamentos', label: 'Dados Gerais' }, // Página existente
      { id: 'entrevistas', label: 'Entrevistas' }, // Nova página
    ],
  },
  {
    id: 'organizational-climate',
    label: 'Clima Organizacional',
    icon: <Smile size={20} />,
    subItems: [
      { id: 'climate-general-data', label: 'Dados gerais' }, // Página existe
      { id: 'climate-detail', label: 'Detalhamento', disabled: true },
      { id: 'mood-analysis', label: 'Análise de Humor', disabled: true },
      {
        id: 'exit-interview',
        label: 'Entrevista de desligamento',
        disabled: true,
      },
    ],
  },
  {
    id: 'health',
    label: 'Saúde',
    icon: <Heart size={20} />,
    disabled: true,
    subItems: [
      { id: 'health-general-data', label: 'Dados gerais', disabled: true },
      { id: 'health-detail', label: 'Detalhamento', disabled: true },
      { id: 'critical-cases', label: 'Casos críticos', disabled: true },
      { id: 'welcoming', label: 'Acolhimento', disabled: true },
      { id: 'hspw-protocols', label: 'Protocolos HSPW', disabled: true },
    ],
  },
  {
    id: 'hspw-platform',
    label: 'Plataforma HSPW',
    icon: <Monitor size={20} />,
    subItems: [
      { id: 'platform-usage', label: 'Uso da plataforma', disabled: true },
      { id: 'platform-surveys', label: 'Pesquisas', disabled: true },
      { id: 'communication', label: 'Comunicação', disabled: true },
      { id: 'platform-gamification', label: 'Gamificação' }, // Página existe
      { id: 'connectz', label: 'Conectz', disabled: true },
      { id: 'support', label: 'Suporte', disabled: true },
    ],
  },
  {
    id: 'harassment-report',
    label: 'Denúncia de Assédio',
    icon: <Shield size={20} />,
    disabled: true,
    subItems: [],
  },
  {
    id: 'contact-hr',
    label: 'Fale com o RH',
    icon: <MessageCircle size={20} />,
  },
];

export const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onToggle,
  activeItem = 'dashboard',
  onItemClick,
}) => {
  const isMobile = useIsMobile();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [clickedItem, setClickedItem] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0 });
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // No mobile, sempre mostra como expandido quando aberto
  const effectiveIsOpen = isMobile ? true : isOpen;

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Fechar tooltip quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isOpen && clickedItem) {
        const target = event.target as HTMLElement;
        if (
          !target.closest('[data-sidebar]') &&
          !target.closest('[data-tooltip]')
        ) {
          setClickedItem(null);
          setHoveredItem(null);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen, clickedItem]);

  const showSubmenu = (itemId: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemId) ? prev : [...prev, itemId]
    );
  };

  const hideSubmenu = (itemId: string) => {
    setExpandedItems((prev) => prev.filter((id) => id !== itemId));
  };

  const handleItemHover = (itemId: string, event: React.MouseEvent) => {
    if (!isOpen && !isMobile) {
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltipPosition({ top: rect.top });
      setHoveredItem(itemId);
    }
  };

  const handleItemLeave = () => {
    if (!isOpen && !isMobile) {
      // Se foi clicado, não fecha automaticamente
      if (clickedItem) {
        setHoveredItem(null);
        return;
      }

      // Delay para permitir hover no tooltip
      hoverTimeoutRef.current = setTimeout(() => {
        setHoveredItem(null);
      }, 150);
    }
  };

  const handleTooltipEnter = () => {
    // Cancela o timeout e mantém o tooltip aberto
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handleTooltipLeave = () => {
    // Fecha o tooltip imediatamente quando sai dele
    if (!isOpen) {
      setHoveredItem(null);
      setClickedItem(null);
    }
  };

  const handleItemClick = (item: any, event: React.MouseEvent) => {
    if (!isOpen && !isMobile && item.subItems && item.subItems.length > 0) {
      // Se está colapsado no desktop e tem subitens, mostra tooltip
      event.preventDefault();
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltipPosition({ top: rect.top });
      setClickedItem(item.id);
      setHoveredItem(item.id);
    } else if (!item.subItems) {
      // Se não tem subitens, navega normalmente
      onItemClick?.(item.id);
      setClickedItem(null);
      setHoveredItem(null);

      // No mobile, fecha a sidebar após navegar
      if (isMobile) {
        onToggle();
      }
    }
  };

  const handleSubItemClick = (subItemId: string) => {
    onItemClick?.(subItemId);

    // No mobile, fecha a sidebar após navegar
    if (isMobile) {
      onToggle();
    }
  };

  // Função para verificar se um subitem está ativo
  const isSubItemActive = (subItemId: string) => {
    // Lista de subitens que devem ser destacados quando a página correspondente está ativa
    const subItemToPageMap: { [key: string]: string } = {
      home: 'home',
      'people-alerts': 'people-alerts',
      'personal-data': 'personal-data',
      'leadership-indicators': 'leadership-indicators',
    };

    return activeItem === subItemToPageMap[subItemId];
  };

  // Função para verificar se um item é pai do item ativo
  const isParentOfActiveItem = (parentId: string) => {
    const activeItemToParentMap: { [key: string]: string } = {
      home: 'people',
      'people-alerts': 'people',
      'personal-data': 'people',
      'leadership-indicators': 'people',
      performance: 'performance',
      'general-costs': 'costs',
      'absenteeism-cost': 'costs',
      'turnover-cost': 'costs',
      'absenteeism-alerts': 'absenteeism-menu',
      'absenteeism-data': 'absenteeism-menu',
      'medical-certificates': 'absenteeism-menu',
      'turnover-general-data': 'turnover',
      'turnover-exit-interview': 'turnover',
      desligamentos: 'desligamentos-menu',
      entrevistas: 'desligamentos-menu',
      'climate-general-data': 'organizational-climate',
      'climate-detail': 'organizational-climate',
      'mood-analysis': 'organizational-climate',
      'exit-interview': 'organizational-climate',
      'health-general-data': 'health',
      'health-detail': 'health',
      'critical-cases': 'health',
      welcoming: 'health',
      'hspw-protocols': 'health',
      'platform-usage': 'hspw-platform',
      'platform-surveys': 'hspw-platform',
      communication: 'hspw-platform',
      'platform-gamification': 'hspw-platform',
      connectz: 'hspw-platform',
      support: 'hspw-platform',
    };

    return activeItemToParentMap[activeItem] === parentId;
  };

  // Effect para expandir automaticamente o menu pai quando um submenu estiver ativo
  useEffect(() => {
    // Mapeamento de itens ativos para seus menus pai
    const activeItemToParentMap: { [key: string]: string } = {
      home: 'people',
      'people-alerts': 'people',
      'personal-data': 'people',
      'leadership-indicators': 'people',
      performance: 'performance',
      'general-costs': 'costs',
      'absenteeism-cost': 'costs',
      'turnover-cost': 'costs',
      'absenteeism-alerts': 'absenteeism-menu',
      'absenteeism-data': 'absenteeism-menu',
      'medical-certificates': 'absenteeism-menu',
      'turnover-general-data': 'turnover',
      'turnover-exit-interview': 'turnover',
      desligamentos: 'desligamentos-menu',
      entrevistas: 'desligamentos-menu',
      'climate-general-data': 'organizational-climate',
      'climate-detail': 'organizational-climate',
      'mood-analysis': 'organizational-climate',
      'exit-interview': 'organizational-climate',
      'health-general-data': 'health',
      'health-detail': 'health',
      'critical-cases': 'health',
      welcoming: 'health',
      'hspw-protocols': 'health',
      'platform-usage': 'hspw-platform',
      'platform-surveys': 'hspw-platform',
      communication: 'hspw-platform',
      'platform-gamification': 'hspw-platform',
      connectz: 'hspw-platform',
      support: 'hspw-platform',
    };

    const parentMenu = activeItemToParentMap[activeItem];

    // Recolher todos os menus e expandir apenas o menu pai do item ativo
    if (parentMenu) {
      setExpandedItems([parentMenu]);
    } else {
      // Se não há menu pai (item de nível raiz), recolher todos
      setExpandedItems([]);
    }
  }, [activeItem]);

  return (
    <SidebarContainer isOpen={isOpen} data-sidebar>
      <ToggleButton onClick={onToggle}>
        <ChevronRight
          size={11}
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
          }}
        />
      </ToggleButton>

      <Navigation>
        <NavSection>
          <div style={{ height: '12px' }}></div>
          {navigationItems.map((item, index) => (
            <div
              key={item.id}
              onMouseEnter={(e) => {
                if (item.subItems) {
                  if (effectiveIsOpen) {
                    showSubmenu(item.id);
                  } else {
                    handleItemHover(item.id, e);
                  }
                }
              }}
              onMouseLeave={() => {
                if (item.subItems) {
                  if (effectiveIsOpen) {
                    hideSubmenu(item.id);
                  } else {
                    handleItemLeave();
                  }
                }
              }}
            >
              <NavItem
                isActive={
                  item.isActive ||
                  activeItem === item.id ||
                  isParentOfActiveItem(item.id)
                }
                isOpen={effectiveIsOpen}
                disabled={item.disabled}
                onClick={(e) => !item.disabled && handleItemClick(item, e)}
                title={!isOpen ? item.label : undefined}
                style={{
                  marginTop: index === 0 ? '12px' : undefined,
                }}
              >
                <span
                  className="nav-icon"
                  data-has-submenu={item.subItems && item.subItems.length > 0}
                >
                  {item.icon}
                </span>
                <span className="nav-text">{item.label}</span>
                {item.subItems && (
                  <ChevronRight
                    size={12}
                    className={`expand-icon ${
                      expandedItems.includes(item.id) ? 'expanded' : ''
                    }`}
                  />
                )}
              </NavItem>

              {item.subItems && effectiveIsOpen && (
                <SubMenuContainer
                  isOpen={effectiveIsOpen}
                  isExpanded={expandedItems.includes(item.id)}
                >
                  {item.subItems.map((subItem) => (
                    <SubMenuItemButton
                      key={subItem.id}
                      isActive={isSubItemActive(subItem.id)}
                      disabled={subItem.disabled}
                      onClick={() =>
                        !subItem.disabled && handleSubItemClick(subItem.id)
                      }
                    >
                      {subItem.label}
                    </SubMenuItemButton>
                  ))}
                </SubMenuContainer>
              )}
            </div>
          ))}
        </NavSection>
      </Navigation>

      {/* Submenu para sidebar colapsada */}
      {!effectiveIsOpen && (hoveredItem || clickedItem) && (
        <CollapsedSubmenu
          isVisible={!!(hoveredItem || clickedItem)}
          style={{ top: tooltipPosition.top }}
          onMouseEnter={handleTooltipEnter}
          onMouseLeave={handleTooltipLeave}
          data-tooltip
        >
          {(() => {
            const activeTooltipItem = hoveredItem || clickedItem;
            const item = navigationItems.find(
              (nav) => nav.id === activeTooltipItem
            );
            if (!item?.subItems || item.subItems.length === 0) return null;

            return (
              <>
                <SubmenuHeader>{item.label}</SubmenuHeader>
                {item.subItems.map((subItem) => (
                  <SubmenuItem
                    key={subItem.id}
                    isActive={isSubItemActive(subItem.id)}
                    onClick={() => {
                      handleSubItemClick(subItem.id);
                      setHoveredItem(null);
                      setClickedItem(null);
                    }}
                  >
                    {subItem.label}
                  </SubmenuItem>
                ))}
              </>
            );
          })()}
        </CollapsedSubmenu>
      )}
    </SidebarContainer>
  );
};
